/**
 * Adapter between the API client shape (raw Prisma) and the flattened UI `Client`.
 *
 * The clients endpoint does not expose money rollups (revenue, ad-spend) or the
 * single `service` the UI shows. Those come from payments/wallets/ad-spend and
 * the clientServices relation (Phase 5.2), so they default here.
 */

import type {
  Client,
  ClientLevel,
  ClientProjectRef,
  ClientStatus,
  ClientSummary,
  ClientType,
  Currency,
  ProjectStatus,
  ServiceRef,
} from "@/types";
import type { ClientFormValues } from "./components/ClientFormModal";

export interface ApiClientService {
  service?: { id?: string | null; name?: string | null } | null;
}

export interface ApiWallet {
  balanceUsd?: number | string | null;
}

/** Spec §2 summary as returned by the API (decimals may arrive as strings). */
export interface ApiClientSummary {
  totalAgreed?: number | string | null;
  paid?: number | string | null;
  remaining?: number | string | null;
  spentDzd?: number | string | null;
  tasksTotal?: number | null;
  tasksDone?: number | null;
  progress?: number | null;
}

export interface ApiClientProject {
  id: string;
  name: string;
  status: ProjectStatus;
  budget?: number | string | null;
  startDate?: string | null;
  expectedDelivery?: string | null;
}

/** Fields of the API client we read. List returns scalars; detail adds relations. */
export interface ApiClient {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  tiktokUrl?: string | null;
  type: ClientType;
  niche?: string | null;
  level?: ClientLevel | null;
  status: ClientStatus;
  monthlyFee?: number | string | null;
  currency?: Currency;
  startDate?: string | null;
  notes?: string | null;
  photo?: string | null;
  createdAt?: string | null;
  createdById?: string | null;
  wallet?: ApiWallet | null;
  clientServices?: ApiClientService[] | null;
  summary?: ApiClientSummary | null;
  lead?: { id: string } | null;
  projects?: ApiClientProject[] | null;
  /** spec §2.2 — team members responsible for this client */
  team?: Array<{ id: string; name: string; role: string }> | null;
}

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

function formatSince(api: ApiClient): string {
  const raw = api.startDate ?? api.createdAt;
  if (!raw) return "-";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("fr-FR", { month: "short", year: "numeric" });
}

function blankToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}

/** Map an API client to the flattened UI Client. */
export function toClient(api: ApiClient): Client {
  return {
    id: api.id,
    name: api.name,
    email: api.email ?? undefined,
    phone: api.phone ?? undefined,
    facebookUrl: api.facebookUrl ?? undefined,
    instagramUrl: api.instagramUrl ?? undefined,
    tiktokUrl: api.tiktokUrl ?? undefined,
    type: api.type,
    niche: api.niche ?? undefined,
    level: api.level ?? undefined,
    status: api.status,
    // spec consolidée §1 — « Services associés » (liste ET détail les incluent)
    services: toServiceRefs(api.clientServices),
    monthlyFee: toNumber(api.monthlyFee),
    currency: api.currency ?? "DZD",
    startDate: api.startDate ?? undefined,
    notes: api.notes ?? undefined,
    photo: api.photo ?? undefined,
    revenue: 0,
    adSpendUsd: 0,
    walletUsd: toNumber(api.wallet?.balanceUsd),
    ownerId: api.createdById ?? undefined,
    createdAt: api.createdAt ?? undefined,
    since: formatSince(api),
    summary: api.summary ? toSummary(api.summary) : undefined,
    leadId: api.lead?.id,
    projects: api.projects?.map(toProjectRef),
    managers: api.team?.map((m) => m.name),
  };
}

function toServiceRefs(list: ApiClientService[] | null | undefined): ServiceRef[] {
  return (list ?? [])
    .map((cs) => cs.service)
    .filter((s): s is { id: string; name: string } => Boolean(s?.id && s?.name))
    .map((s) => ({ id: s.id, name: s.name }));
}

function toSummary(api: ApiClientSummary): ClientSummary {
  return {
    totalAgreed: toNumber(api.totalAgreed),
    paid: toNumber(api.paid),
    remaining: toNumber(api.remaining),
    spentDzd: toNumber(api.spentDzd),
    tasksTotal: api.tasksTotal ?? 0,
    tasksDone: api.tasksDone ?? 0,
    progress: api.progress ?? 0,
  };
}

function toProjectRef(api: ApiClientProject): ClientProjectRef {
  return {
    id: api.id,
    name: api.name,
    status: api.status,
    budget: api.budget === null || api.budget === undefined ? undefined : toNumber(api.budget),
    startDate: api.startDate ?? undefined,
    expectedDelivery: api.expectedDelivery ?? undefined,
  };
}

/**
 * Map UI form values to the API create/update body.
 *
 * `serviceIds` n'est envoyé qu'à la CRÉATION : en édition, les services passent
 * par PATCH /clients/:id/services (permission dédiée clients.services-manage).
 * Le « Modèle de contrat » a été retiré (spec consolidée §2).
 */
export function toClientWriteBody(
  values: ClientFormValues,
  opts: { includeServices?: boolean } = {},
): Record<string, unknown> {
  return {
    name: values.name.trim(),
    email: blankToNull(values.email),
    phone: blankToNull(values.phone),
    facebookUrl: blankToNull(values.facebookUrl),
    instagramUrl: blankToNull(values.instagramUrl),
    tiktokUrl: blankToNull(values.tiktokUrl),
    type: values.type,
    niche: blankToNull(values.niche),
    level: values.level,
    status: values.status,
    ...(opts.includeServices ? { serviceIds: values.serviceIds } : {}),
    monthlyFee: values.monthlyFee,
    currency: values.currency,
    startDate: blankToNull(values.startDate),
    notes: blankToNull(values.notes),
    photo: blankToNull(values.photo),
  };
}
